import { useState } from "react";
import { ShipperCreditProfile, getCreditProfileCompletion } from "@/hooks/useShipperCreditProfile";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Building2,
  FileCheck2,
  Landmark,
  Users,
  ShieldCheck,
  MapPin,
  Receipt,
  Phone,
  FileText,
  ChevronDown,
  PenLine,
  
  StickyNote,
} from "lucide-react";
import { formatPhoneNumber } from "@/lib/formatPhone";
import { formatYYYYMMDDToLocaleDateString } from "@/lib/dateOnly";

interface Props {
  profile: ShipperCreditProfile | null;
  variant?: "full" | "compact";
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  const isEmpty =
    value === null ||
    value === undefined ||
    value === "" ||
    (typeof value === "number" && isNaN(value));
  return (
    <div className="space-y-0.5 min-w-0">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm text-foreground break-words">
        {isEmpty ? <span className="text-muted-foreground">Not provided</span> : value}
      </div>
    </div>
  );
}

function Section({
  title,
  icon: Icon,
  defaultOpen = false,
  children,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="overflow-hidden">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger className="w-full flex items-center justify-between p-4 hover:bg-muted/40 transition-colors">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Icon className="h-4 w-4 text-primary" />
            {title}
          </div>
          <ChevronDown
            className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
          />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-5 pb-5 pt-1">{children}</div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

const money = (n: number | null | undefined) =>
  n == null || isNaN(n as number) ? null : `$${Number(n).toLocaleString()}`;

const yesNo = (b: boolean | null | undefined) => (b == null ? null : b ? "Yes" : "No");

const fmtDate = (v: string | null | undefined) => {
  if (!v) return null;
  // Handles both YYYY-MM-DD and ISO timestamps
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
    return formatYYYYMMDDToLocaleDateString(v);
  }
  const d = new Date(v);
  return isNaN(d.getTime()) ? v : d.toLocaleString();
};

export function CreditProfileSummary({ profile, variant = "full" }: Props) {
  const completion = getCreditProfileCompletion(profile);

  if (!profile) {
    return (
      <Card className="p-6 text-sm text-muted-foreground">
        No credit profile on file yet.
      </Card>
    );
  }

  // Compact variant keeps the legacy behavior (used by broker preview surfaces)
  if (variant === "compact") {
    return (
      <div className="space-y-4">
        <Card className="p-4 flex items-center justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-foreground">Credit Profile</span>
              {completion.isComplete ? (
                <Badge variant="secondary">Complete</Badge>
              ) : (
                <Badge variant="outline">{completion.percent}% complete</Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Shared with brokers when you award them a load.
            </p>
          </div>
          <div className="w-32">
            <Progress value={completion.percent} className="h-2" />
          </div>
        </Card>

        <Card className="p-5 space-y-4">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Building2 className="h-4 w-4 text-primary" /> Legal identity
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Field label="Legal name" value={profile.legal_name} />
            <Field label="DBA" value={profile.dba} />
            <Field label="EIN" value={profile.ein} />
            <Field label="Structure" value={profile.business_structure?.toUpperCase()} />
            <Field label="State of inc." value={profile.state_of_incorporation} />
            <Field label="Years in business" value={profile.years_in_business} />
            <Field label="DUNS" value={profile.duns_number} />
          </div>
        </Card>

        <Card className="p-5 space-y-4">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Users className="h-4 w-4 text-primary" /> Authorized signer & AP contact
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Field label="Signer name" value={profile.signer_name} />
            <Field label="Signer title" value={profile.signer_title} />
            <Field label="Signer email" value={profile.signer_email} />
            <Field label="Signer phone" value={formatPhoneNumber(profile.signer_phone)} />
            <Field label="AP contact" value={profile.ap_contact_name} />
            <Field label="AP email" value={profile.ap_contact_email} />
            <Field label="AP phone" value={formatPhoneNumber(profile.ap_contact_phone)} />
          </div>
        </Card>
      </div>
    );
  }

  const billingSame = profile.billing_same_as_physical === true;

  return (
    <div className="space-y-3">
      {/* Status header (always visible) */}
      <Card className="p-4 flex items-center justify-between flex-wrap gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium text-foreground">Credit Profile</span>
            {completion.isComplete ? (
              <Badge variant="secondary">Complete</Badge>
            ) : (
              <Badge variant="outline">{completion.percent}% complete</Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Shared with brokers when you award them a load. Click any section to expand.
          </p>
        </div>
        <div className="w-32">
          <Progress value={completion.percent} className="h-2" />
        </div>
      </Card>

      <Section title="Legal Identity" icon={Building2}>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Field label="Legal name" value={profile.legal_name} />
          <Field label="DBA" value={profile.dba} />
          <Field label="EIN" value={profile.ein} />
          <Field label="Business structure" value={profile.business_structure?.toUpperCase()} />
          <Field label="State of incorporation" value={profile.state_of_incorporation} />
          <Field label="Years in business" value={profile.years_in_business} />
          <Field label="Business started" value={fmtDate(profile.business_started_on)} />
          <Field label="Industry" value={profile.industry} />
          <Field label="DUNS number" value={profile.duns_number} />
          <div className="md:col-span-3">
            <Field label="Commodities shipped" value={profile.commodities_shipped} />
          </div>
        </div>
      </Section>

      <Section title="Company Contact" icon={Phone}>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Field label="Phone" value={formatPhoneNumber(profile.company_phone)} />
          <Field label="Email" value={profile.company_email} />
          <Field label="Website" value={profile.company_website} />
          <Field label="Fax" value={formatPhoneNumber(profile.company_fax)} />
          <Field label="Sales rep" value={profile.sales_rep_name} />
        </div>
      </Section>

      <Section title="Physical Address" icon={MapPin}>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Field label="Address line 1" value={profile.physical_address_line1} />
          </div>
          <Field label="Address line 2" value={profile.physical_address_line2} />
          <Field label="City" value={profile.physical_city} />
          <Field label="State" value={profile.physical_state} />
          <Field label="Postal code" value={profile.physical_postal_code} />
          <Field label="Country" value={profile.physical_country} />
        </div>
      </Section>

      <Section title="Billing Address" icon={MapPin}>
        {billingSame ? (
          <p className="text-sm text-muted-foreground">Same as physical address.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Field label="Address line 1" value={profile.billing_address_line1} />
            </div>
            <Field label="Address line 2" value={profile.billing_address_line2} />
            <Field label="City" value={profile.billing_city} />
            <Field label="State" value={profile.billing_state} />
            <Field label="Postal code" value={profile.billing_postal_code} />
            <Field label="Country" value={profile.billing_country} />
          </div>
        )}
      </Section>

      <Section title="AP & Authorized Signer" icon={Users}>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Field label="AP contact" value={profile.ap_contact_name} />
          <Field label="AP email" value={profile.ap_contact_email} />
          <Field label="AP phone" value={formatPhoneNumber(profile.ap_contact_phone)} />
          <Field label="Signer name" value={profile.signer_name} />
          <Field label="Signer title" value={profile.signer_title} />
          <Field label="Signer email" value={profile.signer_email} />
          <Field label="Signer phone" value={formatPhoneNumber(profile.signer_phone)} />
        </div>
      </Section>

      <Section title="Payment Terms & Volume" icon={FileCheck2}>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Field
            label="Payment terms"
            value={profile.payment_terms_days ? `Net ${profile.payment_terms_days}` : null}
          />
          <Field label="Preferred method" value={profile.preferred_payment_method?.toUpperCase()} />
          <Field label="Factoring friendly" value={yesNo(profile.factoring_friendly)} />
          <Field label="Annual freight spend" value={money(profile.annual_freight_spend_usd)} />
          <Field label="Avg loads / month" value={profile.avg_loads_per_month} />
          <Field label="Requested credit limit" value={money(profile.credit_limit_request_usd)} />
        </div>
      </Section>

      <Section title="Billing Requirements" icon={Receipt}>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Field label="Requires PO number" value={yesNo(profile.requires_po_number)} />
          <Field label="Requires invoice docs" value={yesNo(profile.requires_invoice_docs)} />
          <div className="md:col-span-3">
            <Field label="Invoice doc notes" value={profile.invoice_docs_notes} />
          </div>
          <div className="md:col-span-3">
            <Field label="Special billing requirements" value={profile.billing_special_requirements} />
          </div>
        </div>
      </Section>

      <Section title="Bank Reference" icon={Landmark}>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Field label="Bank" value={profile.bank_name} />
          <Field label="Contact" value={profile.bank_contact_name} />
          <Field label="Phone" value={formatPhoneNumber(profile.bank_contact_phone)} />
          <Field label="Email" value={profile.bank_contact_email} />
          <Field
            label="Account (last 4)"
            value={profile.bank_account_last4 ? `••••${profile.bank_account_last4}` : null}
          />
          <Field
            label="Routing (last 4)"
            value={profile.bank_routing_last4 ? `••••${profile.bank_routing_last4}` : null}
          />
        </div>
      </Section>

      <Section title="Trade References" icon={FileText}>
        <div className="space-y-3">
          {[1, 2, 3].map((n) => {
            const p = profile as any;
            return (
              <div
                key={n}
                className="grid grid-cols-2 md:grid-cols-5 gap-3 py-3 border-t first:border-t-0 first:pt-0"
              >
                <Field label={`Ref ${n} company`} value={p[`trade_ref_${n}_company`]} />
                <Field label="Contact" value={p[`trade_ref_${n}_contact`]} />
                <Field label="Phone" value={formatPhoneNumber(p[`trade_ref_${n}_phone`])} />
                <Field label="Email" value={p[`trade_ref_${n}_email`]} />
                <Field label="Account #" value={p[`trade_ref_${n}_account`]} />
              </div>
            );
          })}
        </div>
      </Section>


      <Section title="Authorized Signature" icon={PenLine}>
        <div className="space-y-4">
          {profile.signer_signature_text ? (
            <div className="rounded-md border bg-muted/30 px-4 py-3">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
                Typed signature
              </div>
              <div
                className="text-2xl text-foreground"
                style={{ fontFamily: '"Dancing Script", cursive' }}
              >
                {profile.signer_signature_text}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No signature on file.</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Signed at" value={fmtDate(profile.signer_signature_saved_at)} />
            <Field label="IP address" value={profile.signer_signature_ip} />
            <div className="md:col-span-3">
              <Field label="User agent" value={profile.signer_signature_user_agent} />
            </div>
          </div>
        </div>
      </Section>

      <Section title="Notes for Brokers" icon={StickyNote}>
        {profile.notes_for_brokers ? (
          <p className="text-sm text-foreground whitespace-pre-wrap">{profile.notes_for_brokers}</p>
        ) : (
          <p className="text-sm text-muted-foreground">No notes provided.</p>
        )}
      </Section>
    </div>
  );
}
